import { Grid } from "@mui/material";
import { OLButton } from "OLButton";
import { useCampaigns } from "api/campaigns";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { SimulationFields } from "components/Layout/Campaign/CampaignSimulation/types";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useStepper } from "hooks/useStepper";
import { useState } from "react";
import { CampaignTriggers, CampaignTriggersUnion } from "types/campaigns";
import {
  CampaignSimulationStepsIds,
  CampaignSimulationStepsUnion,
  Status,
  StepDetails,
} from "types/stepper";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { Stepper } from "../../../Stepper/Stepper";
import { eventStep, transactionStep } from "../../../Stepper/utils";
import { CampaignTrigger } from "./CampaignTrigger/CampaignTrigger";
import { EventDetails } from "./EventDetails/EventDetails";
import { PersonDetailsSection } from "./MemberDetailsSection/PersonDetailsSection";
import { useStyles } from "./SimulateCampaign.styles";
import { Summary } from "./Summary/Summary";
import { TransactionDetails } from "./TransactionDetails/TransactionDetails";
import { getValidationSchemaByStep, markErrorSteps } from "./utils";

export function SimulateCampaign() {
  const classes = useStyles();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { simulateCampaign } = useCampaigns();
  const [results, setResults] = useState<{}[]>([]);

  const { EVENT, TRIGGER, MEMBER, SUMMARY, TRANSACTION, REFERRED_MEMBER } =
    CampaignSimulationStepsIds;
  const { ACTIVE, NOT_VISITED } = Status;

  const stepsData: StepDetails<CampaignSimulationStepsUnion>[] = [
    { id: TRIGGER, label: "trigger", status: ACTIVE },
    { id: MEMBER, label: "memberDetails", status: NOT_VISITED },
    {
      id: REFERRED_MEMBER,
      label: "referrerDetails",
      status: NOT_VISITED,
    },
    { id: TRANSACTION, label: "transaction", status: NOT_VISITED },
    { id: SUMMARY, label: "summary", status: NOT_VISITED },
  ];

  const {
    ref,
    steps,
    isLastStep,
    isFirstStep,
    prevStep,
    nextStep,
    setSteps,
    backToFirstStep,
    setStepError,
    activeStep,
  } = useStepper(stepsData);

  const initialValues: SimulationFields = {
    custom_event: { eventDate: null },
    customer: {
      birthDate: null,
      levelAchievementDate: null,
      registeredDate: null,
      firstTransactionDate: null,
      lastTransactionDate: null,
      segments: [],
      labels: [],
      id: null,
    },
    internal_event: { eventDate: null, type: "" },
    referrer: {
      birthDate: null,
      levelAchievementDate: null,
      registeredDate: null,
      firstTransactionDate: null,
      lastTransactionDate: null,
      segments: [],
      labels: [],
    },
    transaction: { purchasedAt: null },
    trigger: "transaction",
  };

  const toggleTriggerStep = (trigger: CampaignTriggersUnion) => {
    const newSteps = [...steps];

    switch (trigger) {
      case CampaignTriggers.TRANSACTION:
      case CampaignTriggers.RETURN_TRANSACTION:
        const eventStepIndex = steps.findIndex((step) => step.id === EVENT);

        if (eventStepIndex < 0) return;
        newSteps.splice(eventStepIndex, 1, transactionStep);
        return setSteps(newSteps);
      case CampaignTriggers.CUSTOM_EVENT:
      case CampaignTriggers.INTERNAL_EVENT:
        const transactionStepIndex = steps.findIndex(
          (step) => step.id === TRANSACTION,
        );

        if (transactionStepIndex < 0) return;

        newSteps.splice(transactionStepIndex, 1, eventStep);
        return setSteps(newSteps);
    }
  };

  const activeTab = steps.find((step) => step.status === ACTIVE);
  const isLastFormStep = activeTab?.id === steps[steps.length - 2].id;
  const validationSchema = getValidationSchemaByStep(activeStep.id, message);

  return (
    <div ref={ref}>
      <TopHeader title={message({ id: "campaignSimulator" })} />
      <div className={classes.formWrapper}>
        <Formik
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          initialStatus={{}}
          initialValues={initialValues}
          onSubmit={async ({ ...values }, { setSubmitting, setErrors }) => {
            setSubmitting(true);
            setErrors({});

            const campaignData = {
              ...values,
            };

            switch (values.trigger) {
              case CampaignTriggers.CUSTOM_EVENT:
                delete campaignData.transaction;
                delete campaignData.internal_event;
                break;
              case CampaignTriggers.INTERNAL_EVENT:
                delete campaignData.transaction;
                delete campaignData.custom_event;
                break;
              case CampaignTriggers.TRANSACTION:
                delete campaignData.custom_event;
                delete campaignData.internal_event;
                delete campaignData.transaction?.documentType;
                delete campaignData.transaction?.linkedDocumentNumber;
                break;
              case CampaignTriggers.RETURN_TRANSACTION:
                delete campaignData.custom_event;
                delete campaignData.internal_event;
                if (campaignData.transaction) {
                  campaignData.transaction.documentType = "return";
                }
            }

            try {
              const response = await simulateCampaign(campaignData);
              notify(
                message({
                  id: response?.data?.effects?.length
                    ? "simulationComplete"
                    : "noData",
                }),
              );
              setResults(response?.data?.effects || []);

              !isLastStep && nextStep();
            } catch (exception) {
              setSubmitting(false);
              notify(getErrorMessage(exception));
              const errors = backendErrors(getBackendErrors(exception));

              setSteps(markErrorSteps(steps, errors));
              setResults([]);
              setErrors(errors);
              focusError();
            }
          }}
        >
          {({
            handleSubmit,
            isSubmitting,
            status,
            dirty,
            setFieldValue,
            values,
            errors,
            setFieldError,
            setValues,
            validateForm,
            setErrors,
            initialValues,
          }) => {
            return (
              <Form onSubmit={handleSubmit}>
                {status?.[""] && (
                  <span className={classes.error}>{status[""]}</span>
                )}
                <Stepper
                  steps={steps}
                  setSteps={async (steps) => {
                    const errors = await validateForm();
                    const areErrors = Object.keys(errors).length > 0;

                    if (areErrors) {
                      setStepError(activeStep.id, true);
                      return setErrors(errors);
                    }

                    setSteps(steps);
                  }}
                />

                {activeTab?.id === TRIGGER && (
                  <CampaignTrigger
                    setFieldValue={setFieldValue}
                    trigger={values.trigger}
                    toggleTriggerStep={toggleTriggerStep}
                  />
                )}
                {activeTab?.id === MEMBER && (
                  <PersonDetailsSection
                    fieldNamePrefix="customer"
                    setFieldValue={setFieldValue}
                    data={values.customer}
                    setValues={(newValues) =>
                      setValues({
                        ...values,
                        ...newValues,
                      })
                    }
                  />
                )}
                {activeTab?.id === REFERRED_MEMBER && (
                  <PersonDetailsSection
                    fieldNamePrefix="referrer"
                    setFieldValue={setFieldValue}
                    data={values.referrer}
                    setValues={(newValues) =>
                      setValues({
                        ...values,
                        ...newValues,
                      })
                    }
                  />
                )}
                {activeTab?.id === TRANSACTION && (
                  <TransactionDetails
                    labels={values?.transaction?.labels}
                    products={values.transaction?.items}
                    errors={errors?.transaction}
                    setFieldError={setFieldError}
                    trigger={values.trigger}
                  />
                )}
                {activeTab?.id === EVENT && (
                  <EventDetails
                    trigger={values.trigger}
                    customEvent={values.custom_event}
                  />
                )}
                {activeTab?.id === SUMMARY && <Summary results={results} />}

                <Grid container spacing={12} justifyContent="flex-end" mb={12}>
                  {!isFirstStep && (
                    <Grid item>
                      <OLButton
                        variant="outlined"
                        onClick={prevStep}
                        data-test-id="previous-step"
                      >
                        {message({ id: "previousStep" })}
                      </OLButton>
                    </Grid>
                  )}

                  <Grid item>
                    {isLastStep ? (
                      <OLButton
                        variant="contained"
                        onClick={backToFirstStep}
                        disabled={isSubmitting}
                        data-test-id="resimulate-campaign"
                      >
                        {message({
                          id: "resimulateCampaign",
                        })}
                      </OLButton>
                    ) : (
                      <OLButton
                        variant="contained"
                        onClick={async () => {
                          if (isLastFormStep) handleSubmit();
                          const errors = await validateForm();
                          const areErrors = Object.keys(errors).length > 0;

                          if (areErrors) {
                            setStepError(activeStep.id, true);
                            return setErrors(errors);
                          }
                          setStepError(activeStep.id, false);

                          nextStep();
                        }}
                        disabled={isSubmitting}
                        data-test-id={
                          isLastFormStep ? "simulate-campaign" : "next-step"
                        }
                      >
                        {message({
                          id: isLastFormStep ? "simulateCampaign" : "nextStep",
                        })}
                      </OLButton>
                    )}
                  </Grid>
                </Grid>
                {!isSubmitting && (
                  <LeaveSiteGuard
                    warnBeforeRefresh
                    isDirty={dirty}
                    initialValues={initialValues}
                    currentValues={values}
                  />
                )}
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
}
